import React from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { QuestLogin } from '@questlabs/react-sdk';
import { useAuth } from '../context/AuthContext';
import questConfig from '../config/questConfig';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiShield, FiUsers, FiTrendingUp } = FiIcons;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');

  const handleLogin = ({ userId, token, newUser }) => {
    // Store authentication data
    const userData = { userId, token, newUser };
    login(userData);
    
    if (newUser) {
      navigate('/onboarding');
    } else if (redirect === 'join') {
      navigate('/pro/dashboard');
    } else {
      navigate('/');
    }
  };

  const features = [
    {
      icon: FiShield,
      title: "Professional Account",
      description: "Access your dashboard and manage your services"
    },
    {
      icon: FiUsers,
      title: "Connect with Clients",
      description: "Build relationships with customers who need your services"
    },
    {
      icon: FiTrendingUp,
      title: "Grow Your Business",
      description: "Increase your earnings and expand your client base"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="min-h-screen flex">
        {/* Left Section - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-10"></div>
          <div className="relative z-10 flex flex-col justify-center px-12">
            <div className="mb-8">
              <Link to="/" className="flex items-center space-x-3 mb-6 group">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm group-hover:bg-opacity-30 transition-all">
                  <SafeIcon icon={FiStar} className="text-white text-2xl" />
                </div>
                <span className="text-3xl font-bold hover:text-blue-100 transition-colors">ServiceList</span>
              </Link>
              <h1 className="text-4xl font-bold mb-4">
                {redirect === 'join' ? 'Join as Professional' : 'Welcome Back!'}
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                {redirect === 'join' 
                  ? 'Create your professional account to start earning and managing your services.'
                  : 'Sign in to access your professional dashboard and manage your services.'
                }
              </p>
            </div>
            
            <div className="space-y-6">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <SafeIcon icon={feature.icon} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{feature.title}</h3>
                    <p className="text-blue-100">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Decorative Elements */}
          <div className="absolute top-10 right-10 w-32 h-32 bg-white bg-opacity-10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-24 h-24 bg-white bg-opacity-10 rounded-full blur-2xl"></div>
        </div>

        {/* Right Section - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Mobile Header */}
            <div className="lg:hidden text-center mb-8">
              <Link to="/" className="flex items-center justify-center space-x-2 mb-4 group">
                <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center group-hover:bg-primary-700 transition-colors">
                  <SafeIcon icon={FiStar} className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">ServiceList</span>
              </Link>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {redirect === 'join' ? 'Join as Professional' : 'Welcome Back!'}
              </h2>
              <p className="text-gray-600">
                {redirect === 'join' ? 'Create your professional account' : 'Sign in to your account'}
              </p>
            </div>

            {/* Quest Login Component */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="hidden lg:block text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {redirect === 'join' ? 'Create Professional Account' : 'Sign In'}
                </h2>
                <p className="text-gray-600">
                  {redirect === 'join' 
                    ? 'Join our network of trusted professionals'
                    : 'Access your professional dashboard'
                  }
                </p>
              </div>
              
              <QuestLogin
                onSubmit={handleLogin}
                email={true}
                google={false}
                accent={questConfig.PRIMARY_COLOR}
              />
              
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  {redirect === 'join' ? (
                    <>
                      Already have an account?{' '}
                      <Link to="/login" className="text-primary-600 font-medium hover:text-primary-700">
                        Sign in here
                      </Link>
                    </>
                  ) : (
                    <>
                      Don't have an account?{' '}
                      <Link to="/join" className="text-primary-600 font-medium hover:text-primary-700">
                        Join as Professional
                      </Link>
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;